import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, FileDown, Receipt, Building2, Shield, User, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  draft: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  sent: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  approved: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  paid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  overdue: "bg-red-500/20 text-red-300 border-red-500/30",
  cancelled: "bg-slate-700/40 text-slate-500 border-slate-700/40",
};

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("body_shop");

  const load = () => api.get(`/invoices?type=${tab}`).then((r) => setInvoices(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF failed");
    }
  };

  const totals = {
    paid: invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0),
    pending: invoices.filter((i) => !["paid", "cancelled"].includes(i.status)).reduce((s, i) => s + i.total, 0),
    count: invoices.length,
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="billing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Billing Center</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Invoices</h1>
          <p className="text-sm text-slate-400 mt-1.5">Body shop & insurance invoices · auto-generated from completed jobs.</p>
        </div>
        <Link to="/invoices/new" data-testid="new-invoice-btn">
          <Button className="bg-blue-600 hover:bg-blue-500">
            <Plus className="w-4 h-4 mr-2" /> New Invoice
          </Button>
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Paid</div>
          <div className="font-display text-2xl font-semibold text-emerald-400 mt-1">${totals.paid.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Pending</div>
          <div className="font-display text-2xl font-semibold text-amber-400 mt-1">${totals.pending.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Count</div>
          <div className="font-display text-2xl font-semibold text-slate-200 mt-1">{totals.count}</div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-800">
        {[
          { key: "body_shop", label: "Body Shop", icon: Building2 },
          { key: "customer", label: "Customer", icon: User },
          { key: "insurance", label: "Insurance", icon: Shield },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 border-b-2 -mb-px ${
                tab === t.key ? "border-blue-500 text-blue-300" : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
        {invoices.length === 0 ? (
          <div className="p-16 text-center">
            <Receipt className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <div className="text-sm text-slate-400">No {tab.replace("_", " ")} invoices yet.</div>
            <Link to="/invoices/new"><Button variant="outline" className="mt-4 border-slate-700">Create first invoice</Button></Link>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/40">
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Invoice #</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Recipient</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Vehicle</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Issued</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Status</th>
                <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Total</th>
                <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-5 py-3 font-mono text-xs text-blue-300">
                    <Link to={`/invoices/${inv.id}`} className="hover:underline">{inv.invoice_number}</Link>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-200">
                    <div>{inv.recipient_name}</div>
                    {inv.customer_name && inv.customer_name !== inv.recipient_name && (
                      <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`inv-customer-${inv.id}`}>
                        Cliente: {inv.customer_name}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-400">
                    {inv.vehicle_year ? `${inv.vehicle_year} ${inv.vehicle_make} ${inv.vehicle_model}` : "—"}
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500 font-mono">{inv.issued_date || (inv.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3">
                    <Badge className={`${statusColor[inv.status]} border text-[10px] uppercase tracking-wider`}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-emerald-300">${inv.total.toFixed(2)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv)} data-testid={`pdf-${inv.id}`} className="text-slate-400 hover:text-blue-300" title="Baixar PDF">
                        <FileDown className="w-3.5 h-3.5" />
                      </Button>
                      {inv.job_id && (!["insurance", "body_shop"].includes(inv.type) || inv.status === "cancelled") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!window.confirm("Gerar Dual Invoice (Seguradora + Body Shop) para este job?")) return;
                            try {
                              const r = await api.post(`/invoices/generate-dual/${inv.job_id}?mode=dual`);
                              const nums = [r.data.insurance_invoice, r.data.body_shop_invoice].filter(Boolean).map((x) => x.invoice_number);
                              toast.success(`Dual Invoice criada: ${nums.join(" + ")}`);
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao gerar dual", { duration: 8000 }); }
                          }}
                          data-testid={`dual-${inv.id}`}
                          className="text-slate-400 hover:text-amber-300"
                          title="Gerar Dual Invoice (Seguradora + Body Shop)"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!window.confirm(`Apagar invoice ${inv.invoice_number} permanentemente? Isso não pode ser desfeito.`)) return;
                          try {
                            await api.delete(`/invoices/${inv.id}`);
                            toast.success(`Invoice ${inv.invoice_number} apagada`);
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao apagar"); }
                        }}
                        data-testid={`delete-inv-${inv.id}`}
                        className="text-slate-400 hover:text-red-400"
                        title="Apagar invoice"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
